import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {initReactI18next} from 'react-i18next';
import translationEN from './locales/en/translationEN.json';
import translationHR from './locales/hr/translationHR.json';
import {isPostHogEnabled, posthog} from './utils/posthog/posthog';

const defaultNS = 'translation' as const;

type TranslationResources = typeof translationEN | typeof translationHR;
type LeafTranslationKeys<T> = {
    [K in keyof T & string]: T[K] extends string ? K : T[K] extends Record<string, unknown> ? `${K}.${LeafTranslationKeys<T[K]>}` : never;
}[keyof T & string];
type EnLeafTranslation = LeafTranslationKeys<typeof translationEN>;
type HrLeafTranslation = LeafTranslationKeys<typeof translationHR>;
export type TypedTranslation = Extract<EnLeafTranslation, HrLeafTranslation>;

declare module 'i18next' {
    interface CustomTypeOptions {
        returnNull: false;
        defaultNS: typeof defaultNS;
        resources: {
            translation: TranslationResources;
        };
    }
}

const isDevelopment = process.env.NODE_ENV === 'development';

const reported = new Set<string>();

const I18_NEXT_LNG = 'i18nextLng';

if (!localStorage.getItem(I18_NEXT_LNG)) {
    localStorage.setItem(I18_NEXT_LNG, 'en');
}

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            hr: {translation: translationHR},
            en: {translation: translationEN},
        },
        defaultNS,
        detection: {order: ['localStorage', 'navigator']},
        fallbackLng: 'en',
        missingKeyHandler: (lngs, ns, key) => {
            const uniqueKey = `${ns}:${key}`;
            if (reported.has(uniqueKey)) return;
            reported.add(uniqueKey);
            if (isPostHogEnabled()) posthog.capture('missing i18n key', {key, ns, lng: lngs?.[0]});
        },
        parseMissingKeyHandler: (key) => {
            if (isDevelopment) {
                return `[${key}]`;
            }
            return key.split('.').pop() ?? key;
        },
        interpolation: {escapeValue: false},
        returnNull: false,
    });

/** Accepts only valid translation keys; for use in Zod schemas and other runtime contexts where i18n.t cannot be used directly. */
export function tm(key: TypedTranslation): string {
    return key;
}

export default i18n;
